/**
 * Fetching the public key a credential will be sealed to.
 *
 * Shared by every form that seals, so there is one place that decides when the
 * key is read. It is fetched when a form opens rather than when the page loads:
 * the shorter the window between reading a key and sealing to it, the smaller the
 * chance of sealing to one the device has already replaced.
 *
 * That window cannot be closed here, only narrowed — a device can be re-claimed
 * while a form sits open. The server checks the fingerprint on submission, which
 * is what actually closes it.
 */

import { useEffect, useState } from 'react';

import { ApiError, fetchDevice } from './api.ts';

export type DeviceKey =
  | { status: 'loading' }
  | { status: 'ready'; publicKey: string; fingerprint: string }
  /** The device has never claimed itself, so it has no key. Not an error. */
  | { status: 'absent' }
  | { status: 'error'; message: string };

export function useDeviceKey(deviceId: string, active: boolean): DeviceKey {
  const [key, setKey] = useState<DeviceKey>({ status: 'loading' });

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    setKey({ status: 'loading' });
    void (async () => {
      try {
        const device = await fetchDevice(deviceId);
        if (cancelled) return;

        if (!device.public_key || !device.publicKeyFingerprint) {
          setKey({ status: 'absent' });
          return;
        }
        setKey({
          status: 'ready',
          publicKey: device.public_key,
          fingerprint: device.publicKeyFingerprint,
        });
      } catch (error) {
        if (cancelled) return;
        setKey({
          status: 'error',
          message: error instanceof ApiError ? error.message : 'Could not load the device key.',
        });
      }
    })();

    return () => {
      // Guards against a slow response landing after the form closed and
      // overwriting a key a later open has already fetched.
      cancelled = true;
    };
  }, [deviceId, active]);

  return key;
}
